import { supabase } from '../lib/supabase';
import apiClient from './client';

export interface SsoConfiguration {
  id: string;
  organization_id?: string | null;
  tenant_id: string;
  client_id: string;
  client_secret?: string;
  is_active: boolean;
  allow_local_login: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface SsoRoleMapping {
  id: string;
  sso_config_id: string;
  azure_group_id: string;
  azure_group_name: string;
  compass_role: string;
  created_at?: string;
}

export const ssoApi = {
  getConfiguration: async (organizationId?: string): Promise<SsoConfiguration | null> => {
    if (supabase) {
      try {
        let query = supabase.from('sso_configurations').select('*');
        if (organizationId && organizationId !== 'ALL') {
          query = query.eq('organization_id', organizationId);
        }
        const { data, error } = await query.maybeSingle();
        if (!error && data) return data as SsoConfiguration;
      } catch {
        // Fall through
      }
    }

    try {
      const { data } = await apiClient.get<SsoConfiguration>('/sso', {
        params: { organizationId }
      });
      return data;
    } catch {
      return null;
    }
  },

  saveConfiguration: async (payload: Partial<SsoConfiguration>): Promise<SsoConfiguration> => {
    if (supabase) {
      const record = {
        ...payload,
        updated_at: new Date().toISOString()
      };
      const { data, error } = await supabase
        .from('sso_configurations')
        .upsert(record)
        .select()
        .single();

      if (error) throw error;
      return data as SsoConfiguration;
    }

    const { data } = await apiClient.post<SsoConfiguration>('/sso', payload);
    return data;
  },

  getRoleMappings: async (ssoConfigId?: string): Promise<SsoRoleMapping[]> => {
    if (supabase) {
      try {
        let query = supabase.from('sso_role_mappings').select('*');
        if (ssoConfigId) {
          query = query.eq('sso_config_id', ssoConfigId);
        }
        const { data, error } = await query;
        if (!error && data) return data as SsoRoleMapping[];
      } catch {
        // Fall through
      }
    }

    try {
      const { data } = await apiClient.get<SsoRoleMapping[]>('/sso/role-mappings', {
        params: { ssoConfigId }
      });
      return data;
    } catch {
      return [];
    }
  },

  createRoleMapping: async (payload: Partial<SsoRoleMapping>): Promise<SsoRoleMapping> => {
    if (supabase) {
      const { data, error } = await supabase
        .from('sso_role_mappings')
        .insert(payload)
        .select()
        .single();

      if (error) throw error;
      return data as SsoRoleMapping;
    }

    const { data } = await apiClient.post<SsoRoleMapping>('/sso/role-mappings', payload);
    return data;
  },

  deleteRoleMapping: async (id: string): Promise<{ success: boolean }> => {
    if (supabase) {
      const { error } = await supabase.from('sso_role_mappings').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    }

    const { data } = await apiClient.delete<{ success: boolean }>(`/sso/role-mappings/${id}`);
    return data;
  },

  getGroupMappings: async (provider?: string): Promise<any[]> => {
    if (supabase) {
      try {
        let query = supabase.from('sso_group_mappings').select('*');
        if (provider) {
          query = query.eq('provider', provider);
        }
        const { data, error } = await query;
        if (!error && data) return data;
      } catch {
        // Fall through
      }
    }

    try {
      const { data } = await apiClient.get<any[]>('/sso/group-mappings', {
        params: { provider }
      });
      return data;
    } catch {
      return [];
    }
  },

  createGroupMapping: async (payload: any): Promise<any> => {
    if (supabase) {
      const { data, error } = await supabase
        .from('sso_group_mappings')
        .insert(payload)
        .select()
        .single();

      if (error) throw error;
      return data;
    }

    const { data } = await apiClient.post<any>('/sso/group-mappings', payload);
    return data;
  },

  deleteGroupMapping: async (id: string): Promise<{ success: boolean }> => {
    if (supabase) {
      const { error } = await supabase.from('sso_group_mappings').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    }

    const { data } = await apiClient.delete<{ success: boolean }>(`/sso/group-mappings/${id}`);
    return data;
  },

  getSsoUsers: async (): Promise<any[]> => {
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from('sso_identities')
          .select('*, user:profiles(*)');

        if (!error && data) return data;
      } catch {
        // Fall through
      }
    }

    try {
      const { data } = await apiClient.get<any[]>('/sso/users');
      return data;
    } catch {
      return [];
    }
  },

  getOAuthSettings: async (organizationId?: string): Promise<any[]> => {
    if (supabase) {
      try {
        let query = supabase.from('oauth_settings').select('*');
        if (organizationId && organizationId !== 'ALL') {
          query = query.eq('organization_id', organizationId);
        }
        const { data, error } = await query;
        if (!error && data) return data;
      } catch {
        // Fall through
      }
    }

    try {
      const { data } = await apiClient.get<any[]>('/sso/oauth', {
        params: { organizationId }
      });
      return data;
    } catch {
      return [];
    }
  },

  saveOAuthSetting: async (payload: any): Promise<any> => {
    if (supabase) {
      const { data, error } = await supabase
        .from('oauth_settings')
        .upsert(payload)
        .select()
        .single();

      if (error) throw error;
      return data;
    }

    const { data } = await apiClient.post<any>('/sso/oauth', payload);
    return data;
  },

  deleteOAuthSetting: async (id: string): Promise<{ success: boolean }> => {
    if (supabase) {
      const { error } = await supabase.from('oauth_settings').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    }

    const { data } = await apiClient.delete<{ success: boolean }>(`/sso/oauth/${id}`);
    return data;
  }
};
